import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PLATFORMS, POST_STATUSES } from "./constants";

export interface SocialPost {
  id?: string;
  campaign_id: string | null;
  account_id: string | null;
  platform: string;
  geo_type: string | null;
  state_code: string | null;
  city_name: string | null;
  content: string;
  media_urls: string[];
  scheduled_at: string | null;
  status: string;
  approved_by: string | null;
  denial_reason: string | null;
  ai_generated: boolean;
  notes: string | null;
}

const BLANK: SocialPost = {
  campaign_id: null,
  account_id: null,
  platform: 'x',
  geo_type: null,
  state_code: null,
  city_name: null,
  content: '',
  media_urls: [],
  scheduled_at: null,
  status: 'draft',
  approved_by: null,
  denial_reason: null,
  ai_generated: false,
  notes: null,
};

interface Campaign { id: string; name: string; }
interface Account { id: string; platform: string; username: string | null; handle: string | null; state_code: string | null; }

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post?: SocialPost | null;
  campaigns: Campaign[];
  accounts: Account[];
  onSave: (data: SocialPost) => void;
  isPending?: boolean;
}

export function PostDialog({ open, onOpenChange, post, campaigns, accounts, onSave, isPending }: Props) {
  const [form, setForm] = useState<SocialPost>(BLANK);

  useEffect(() => {
    if (post) {
      setForm(post);
    } else {
      setForm(BLANK);
    }
  }, [post, open]);

  const set = (key: keyof SocialPost, value: unknown) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const selectedPlatform = PLATFORMS.find(p => p.id === form.platform);
  const charLimit = selectedPlatform?.charLimit ?? 280;
  const charCount = form.content.length;
  const charPercent = Math.min(100, (charCount / charLimit) * 100);

  const filteredAccounts = accounts.filter(a => !form.platform || a.platform === form.platform);

  const handleSubmit = () => {
    if (!form.content.trim()) return;
    onSave(form);
  };

  const isEditing = !!post?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Post' : 'New Post'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update this post.' : 'Draft a new social media post.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Platform + Account row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Platform *</Label>
              <Select value={form.platform} onValueChange={v => { set('platform', v); set('account_id', null); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: p.color === '#000000' ? '#374151' : p.color }}
                        />
                        {p.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Account</Label>
              <Select
                value={form.account_id ?? ''}
                onValueChange={v => set('account_id', v || null)}
              >
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No specific account</SelectItem>
                  {filteredAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.username ?? a.handle ?? a.id}
                      {a.state_code ? ` (${a.state_code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Campaign */}
          <div className="space-y-1">
            <Label>Campaign (optional)</Label>
            <Select
              value={form.campaign_id ?? ''}
              onValueChange={v => set('campaign_id', v || null)}
            >
              <SelectTrigger><SelectValue placeholder="No campaign" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">No campaign</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Content */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Content *</Label>
              <span className={`text-xs ${charCount > charLimit ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>
                {charCount.toLocaleString()} / {charLimit.toLocaleString()}
              </span>
            </div>
            <Textarea
              value={form.content}
              onChange={e => set('content', e.target.value)}
              placeholder="Write your post content here..."
              rows={5}
              className={charCount > charLimit ? 'border-red-300' : ''}
            />
            {/* Character meter */}
            <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  charPercent > 100 ? 'bg-red-500' : charPercent > 80 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(100, charPercent)}%` }}
              />
            </div>
          </div>

          {/* Scheduled At + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Scheduled At</Label>
              <Input
                type="datetime-local"
                value={form.scheduled_at
                  ? new Date(form.scheduled_at).toISOString().slice(0, 16)
                  : ''
                }
                onChange={e => set('scheduled_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>

            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {POST_STATUSES.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* AI generated flag */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="ai-generated"
              checked={form.ai_generated}
              onCheckedChange={checked => set('ai_generated', !!checked)}
            />
            <label htmlFor="ai-generated" className="text-sm cursor-pointer">AI-generated content</label>
          </div>

          {/* Denial reason (only if denied) */}
          {form.status === 'denied' && (
            <div className="space-y-1">
              <Label>Denial Reason</Label>
              <Textarea
                value={form.denial_reason ?? ''}
                onChange={e => set('denial_reason', e.target.value || null)}
                placeholder="Why was this post denied?"
                rows={2}
              />
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={form.notes ?? ''}
              onChange={e => set('notes', e.target.value || null)}
              placeholder="Internal notes..."
              rows={2}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending || !form.content.trim()}>
            {isPending ? 'Saving...' : isEditing ? 'Update Post' : 'Create Post'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
